import { Stack } from "@mui/material";
import { TFunction } from "i18next";
import { Instance } from "mobx-state-tree";
import React from "react";
import { EditBOUserStore } from "../store/EditBOUserStore";
import {
  AutoCompleteInputField,
  Spacing,
  TextInputField,
} from "@surya-digital/leo-reactjs-material-ui";
import {
  MAX_USERNAME_CHAR_LIMIT,
  MIN_USERNAME_CHAR_LIMIT,
} from "@pulse/shared-components";
import { BoUserRole } from "@pulse/pulse-rpcs/lib/bo-user-management/boUserRole";
import { getTitleCase } from "../../projects/utils/StringUtils";
import { observer } from "mobx-react";

interface EditBOUserChildComponentProps {
  t: TFunction;
  spacing: Spacing;
  editBOUserStore: Instance<typeof EditBOUserStore>;
}

export const EditBOUserChildComponent = observer(
  ({
    editBOUserStore,
    spacing,
    t,
  }: EditBOUserChildComponentProps): React.ReactElement => {
    return (
      <Stack gap={spacing.spaceLG}>
        <Stack direction="row" gap={spacing.spaceLG}>
          <TextInputField
            name={t("common.firstNameLabel")}
            value={editBOUserStore.boUserDetails.firstName}
            type={"text"}
            onTextChange={(firstName: string): void => {
              editBOUserStore.boUserDetails.setFirstName(firstName);
            }}
            placeholder={t("common.firstNamePlaceholder")}
            label={t("common.firstNameLabel")}
            disabled={editBOUserStore.isRPCLoading}
            required={true}
            error={editBOUserStore.isFirstNameInvalid}
            helperText={
              editBOUserStore.isFirstNameInvalid
                ? t("common.invalidFirstNameLengthHelperText", {
                    minFirstNameLength: MIN_USERNAME_CHAR_LIMIT,
                    maxFirstNameLength: MAX_USERNAME_CHAR_LIMIT,
                  })
                : undefined
            }
          />
          <TextInputField
            name={t("common.lastNameLabel")}
            value={editBOUserStore.boUserDetails.lastName}
            type={"text"}
            onTextChange={(lastName: string): void => {
              editBOUserStore.boUserDetails.setLastName(lastName);
            }}
            placeholder={t("common.lastNamePlaceholder")}
            label={t("common.lastNameLabel")}
            disabled={editBOUserStore.isRPCLoading}
            required={true}
            error={editBOUserStore.isLastNameInvalid}
            helperText={
              editBOUserStore.isLastNameInvalid
                ? t("common.invalidLastNameLengthHelperText", {
                    minLastNameLength: MIN_USERNAME_CHAR_LIMIT,
                    maxLastNameLength: MAX_USERNAME_CHAR_LIMIT,
                  })
                : undefined
            }
          />
        </Stack>
        <AutoCompleteInputField
          id="SelectRole"
          label={t("manageUsers.role")}
          placeholder={t("manageUsers.rolePlaceholder")}
          options={Object.values(BoUserRole.BoUserRole).map((role) => {
            return {
              id: role,
              label: getTitleCase(role),
            };
          })}
          disabled={editBOUserStore.isRPCLoading}
          onChange={(role): void => {
            if (role && role.id) {
              editBOUserStore.boUserDetails.setRole(
                BoUserRole.BoUserRole[
                  role.id as keyof typeof BoUserRole.BoUserRole
                ],
              );
            } else {
              editBOUserStore.boUserDetails.resetRole();
              // This else block also gets triggered when the user unselects an option, as in this case `role` is null and `role.id` is undefined.
              // As per the discussion here: https://surya-digital.slack.com/archives/C06EPPC2Y1E/p1712747738387459, this happens because the Leo component calls this onSelect on every change.
              console.warn("Role and Role Id not found.");
            }
          }}
          value={
            editBOUserStore.boUserDetails.selectedRole
              ? {
                  label: getTitleCase(
                    editBOUserStore.boUserDetails.selectedRole,
                  ),
                  id: editBOUserStore.boUserDetails.selectedRole,
                }
              : undefined
          }
          required={true}
        />
      </Stack>
    );
  },
);
